import { useState, useEffect, useRef } from "react";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useT } from "@/lib/i18n";

const ITEM = 44;
const pad = (n) => String(n).padStart(2, "0");
const HOURS = Array.from({ length: 24 }, (_, i) => pad(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad(i));

const Wheel = ({ items, value, onChange, testid }) => {
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const i = items.indexOf(value);
    if (ref.current && i >= 0 && Math.round(ref.current.scrollTop / ITEM) !== i) ref.current.scrollTop = i * ITEM;
  }, [items, value]);

  const onScroll = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!ref.current) return;
      const i = Math.min(items.length - 1, Math.max(0, Math.round(ref.current.scrollTop / ITEM)));
      onChange(items[i]);
    }, 150);
  };

  const pick = (it) => {
    ref.current?.scrollTo({ top: items.indexOf(it) * ITEM, behavior: "smooth" });
    onChange(it);
  };

  return (
    <div className="relative w-20" style={{ height: ITEM * 5 }}>
      <div className="pointer-events-none absolute inset-x-0 rounded-xl bg-[#fbe9dc] border border-[#e8c4ad]" style={{ top: ITEM * 2, height: ITEM }} />
      <div ref={ref} onScroll={onScroll} onScrollEnd={onScroll} data-testid={testid}
        className="kv-wheel relative h-full overflow-y-scroll snap-y snap-mandatory overscroll-contain"
        style={{ paddingTop: ITEM * 2, paddingBottom: ITEM * 2 }}>
        {items.map((it) => (
          <button type="button" key={it} onClick={() => pick(it)} data-testid={`${testid}-${it}`}
            className={`snap-center w-full grid place-items-center font-mono-kv transition-colors ${it === value ? "text-[#2c1810] text-2xl font-semibold" : "text-stone-400 text-xl"}`}
            style={{ height: ITEM }}>
            {it}
          </button>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-0 z-10 bg-gradient-to-b from-white via-white/0 to-white" />
    </div>
  );
};

export const TimeInput = ({ value, onChange, className = "", placeholder = "--:--", allowClear = true, ...rest }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [h, setH] = useState("12");
  const [m, setM] = useState("00");

  const openPicker = () => {
    const valid = value && /^\d{2}:\d{2}$/.test(value);
    setH(valid ? value.slice(0, 2) : pad(new Date().getHours()));
    setM(valid ? value.slice(3, 5) : "00");
    setOpen(true);
  };

  return (
    <>
      <button type="button" onClick={openPicker} {...rest}
        className={`h-10 inline-flex items-center justify-between gap-2 rounded-md border border-input bg-white px-3 font-mono-kv text-base ${className || "w-28"}`}>
        {value ? <span>{value}</span> : <span className="text-stone-400">{placeholder}</span>}
        <Clock className="h-4 w-4 text-stone-400 shrink-0" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[300px] bg-white rounded-2xl" data-testid="time-picker-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{t("time.pick")}</DialogTitle></DialogHeader>
          <div className="flex items-center justify-center gap-3 py-2">
            {open && <Wheel items={HOURS} value={h} onChange={setH} testid="tp-hour" />}
            <span className="text-3xl font-bold text-[#2c1810]">:</span>
            {open && <Wheel items={MINUTES} value={m} onChange={setM} testid="tp-minute" />}
          </div>
          <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
            {value && allowClear ? (
              <Button variant="ghost" onClick={() => { onChange(""); setOpen(false); }} data-testid="tp-clear">{t("time.clear")}</Button>
            ) : <span />}
            <Button className="rounded-full px-6" onClick={() => { onChange(`${h}:${m}`); setOpen(false); }} data-testid="tp-ok">{t("time.ok")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
